import { formatTimeToMinutes } from '@/helpers';
import { mapRateSourceToDTO } from '@/modules/common/mappers/rate-source.mapper';
import { RateSource } from '@/modules/common/types/rate-source';
import { RateMask } from '@/modules/common/utils/rate-mask';
import { normalizeToStartOfDayUTC } from '@/utils/date-formatters';

import { isAssignmentProbabilityVisible } from '../../../../helpers/assignment/probability';
import { mapAssignmentStatusToDTO } from '../../../../mappers';
import { AssignmentFormValues, AssignmentModalProps } from '../assignment-modal';

interface AssignmentCommonPayload {
  userId: number;
  projectId: number;
  startDate: string;
  endDate: string;
  status: ReturnType<typeof mapAssignmentStatusToDTO>;
  doSkipWeekdays: boolean;
  isBillable: boolean;
  label: string;
  minutesPerDay: number;
  note: string;
  isBackgroundWork: boolean;
  rateSource: ReturnType<typeof mapRateSourceToDTO>;
  /** Omitted when the rate is masked or absent — the backend then keeps the stored value. */
  customRate?: number;
  customRateCurrencyCode?: string;
  /** Omitted rather than nulled on create; buildUpdatePayload re-adds it as explicitly nullable. */
  probability?: number;
}

interface PayloadContext {
  values: AssignmentFormValues;
  initialData: AssignmentModalProps['initialData'];
  selectedUserName: string | undefined;
  selectedProjectName: string | undefined;
  selectedProjectColorHex: string | null | undefined;
}

/**
 * Resolves the rate to send, or `undefined` when there is no real value to send.
 *
 * The form is seeded from the API response, so when the backend masked the rate the field holds
 * the masking placeholder rather than a number. Sending it back would persist the placeholder as
 * a genuine rate; omitting the field instead makes the backend keep the stored value.
 */
function resolveCustomRate(values: AssignmentFormValues): number | undefined {
  const parsedRate = parseFloat(values.rate);

  if (Number.isNaN(parsedRate) || RateMask.isMasked(parsedRate)) {
    return undefined;
  }

  return parsedRate;
}

function buildCommonPayload(values: AssignmentFormValues) {
  const minutesPerDay = formatTimeToMinutes(values.scheduledWork);
  const rateSourceDTO = mapRateSourceToDTO(values.rateSource ?? RateSource.UserExternalRate);
  const statusDTO = mapAssignmentStatusToDTO(values.status);
  const customRate = resolveCustomRate(values);
  const probability = isAssignmentProbabilityVisible(values.status)
    ? values.probability
    : undefined;

  // Force isBillable to false when rate source is Custom and rate is 0.
  // Only applies when we actually resolved a rate — an omitted one says nothing about billability.
  const isBillable =
    values.rateSource === RateSource.Custom && customRate === 0 ? false : values.isBillable;

  const customRateCurrencyCode =
    values.rateSource === RateSource.Custom ? values.currency : undefined;

  const payload: AssignmentCommonPayload = {
    userId: Number(values.userId),
    projectId: Number(values.projectId),
    startDate: normalizeToStartOfDayUTC(values.dateRange.from).toISOString(),
    endDate: normalizeToStartOfDayUTC(values.dateRange.to).toISOString(),
    status: statusDTO,
    doSkipWeekdays: values.doSkipWeekdays,
    isBillable,
    label: values.projectId,
    minutesPerDay,
    note: values.note ?? '',
    isBackgroundWork: values.isBackgroundWork,
    rateSource: rateSourceDTO,
    customRate,
    customRateCurrencyCode,
  };

  if (probability !== null && probability !== undefined) payload.probability = probability;

  return payload;
}

function buildUiData({
  initialData,
  selectedUserName,
  selectedProjectName,
  selectedProjectColorHex,
}: Omit<PayloadContext, 'values'>) {
  return {
    userName: selectedUserName || initialData?.userName || '',
    projectName: selectedProjectName || initialData?.projectLabel || '',
    projectColorHex: selectedProjectColorHex || initialData?.projectColorHex || undefined,
  };
}

export function buildCreatePayload(ctx: PayloadContext) {
  return {
    payload: buildCommonPayload(ctx.values),
    ui: buildUiData(ctx),
  };
}

export function buildUpdatePayload(ctx: PayloadContext) {
  const { initialData, values } = ctx;
  const probability = isAssignmentProbabilityVisible(values.status) ? values.probability : null;

  return {
    payload: {
      assignmentId: initialData!.id!,
      syncToken: initialData!.syncToken,
      ...buildCommonPayload(values),
      probability,
    },
    ui: buildUiData(ctx),
  };
}
